import { useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonGroup,
  ListBoxItem,
  Modal,
  Select,
  TextInput,
} from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import { UnsavedProgressConfirmModal } from "../../onboarding/components/UnsavedProgressConfirmModal";
import {
  applyMatchingRowEditDraft,
  createMatchingRowEditDraft,
  isMatchingRowEditDraftDirty,
  validateMatchingRowEditDraft,
  type MatchingRowEditDraft,
} from "../utils/matchingRowEditDraft";
import type { MatchingCsvAction, MatchingRow } from "../types/matching";
import styles from "./MatchingRowEditModal.module.css";

export interface MatchingRowEditModalProps {
  isOpen: boolean;
  row: MatchingRow | null;
  /** Current failure reasons for this row, from the last validation result. */
  rowErrors: readonly string[];
  isProcessing?: boolean;
  onCancel: () => void;
  onSave: (updatedRow: MatchingRow) => void;
}

/**
 * Row-edit modal for Component Matching Import, mirroring Pay Component
 * Import's `PayComponentRowEditModal` structure, accessibility, and visual
 * conventions (field grid, issue summary, Save/Cancel + discard-confirm).
 * Fields are scoped to Matching's user-authored columns — MatchAction,
 * EmployeeCode, ComponentCode, ComponentName, StartDate/FinishDate,
 * TargetAmount. Pay run is bound in the UI, not edited here.
 */
export function MatchingRowEditModal({
  isOpen,
  row,
  rowErrors,
  isProcessing = false,
  onCancel,
  onSave,
}: MatchingRowEditModalProps) {
  const [draft, setDraft] = useState<MatchingRowEditDraft | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  // Keep the last row while the modal closes via isOpen={false}. Returning
  // null as soon as `row` clears unmounts Modal abruptly and can leave a
  // click-blocking React Aria underlay over the sticky footer.
  const [stableRow, setStableRow] = useState<MatchingRow | null>(row);

  useEffect(() => {
    if (row) setStableRow(row);
  }, [row]);

  useEffect(() => {
    if (!isOpen) {
      setDiscardOpen(false);
      return;
    }
    if (!row) return;
    setDraft(createMatchingRowEditDraft(row));
    setDiscardOpen(false);
  }, [isOpen, row]);

  const displayRow = row ?? stableRow;

  const original = useMemo(
    () => (displayRow ? createMatchingRowEditDraft(displayRow) : null),
    [displayRow]
  );
  const isDirty =
    draft && original ? isMatchingRowEditDraftDirty(original, draft) : false;

  const validationErrors = useMemo(
    () => (draft ? validateMatchingRowEditDraft(draft) : []),
    [draft]
  );

  const handleCancel = () => {
    if (isDirty) {
      setDiscardOpen(true);
      return;
    }
    onCancel();
  };

  const handleSave = () => {
    if (!displayRow || !draft || validationErrors.length > 0) return;
    onSave(applyMatchingRowEditDraft(displayRow, draft));
  };

  // Before the first open there is nothing to show. After an open, keep the
  // Modal mounted through isOpen={false} so the underlay tears down cleanly.
  if (!draft || !displayRow) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <Heading slot="title">Edit row {displayRow.fileRow}</Heading>
          </div>

          <div className={styles.modalBody}>
            {rowErrors.length > 0 && (
              <div className={styles.issueSummary} role="alert">
                <strong>Issues on this row</strong>
                <ul className={styles.issueList}>
                  {rowErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.fieldGrid}>
              {/*
                Labels use the design-system `isRequired` flag so required vs
                optional markers stay consistent with
                `validateMatchingRowEditDraft` (and with Work Patterns'
                edit modal). Without `isRequired`, Select/TextInput append
                "(optional)" — which incorrectly marked MatchAction before.
              */}
              <div className={styles.field}>
                <Select
                  label="MatchAction"
                  selectedKey={draft.matchAction || null}
                  onSelectionChange={(key) =>
                    setDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            matchAction: (key as MatchingCsvAction | null) ?? "",
                          }
                        : prev
                    )
                  }
                  isRequired
                  isDisabled={isProcessing}
                >
                  <ListBoxItem id="APPLY">APPLY — set override amount</ListBoxItem>
                  <ListBoxItem id="REAPPLY">
                    REAPPLY — turn override on, keep stored amount
                  </ListBoxItem>
                  <ListBoxItem id="DELETE_KEEP_AMOUNT">
                    DELETE_KEEP_AMOUNT — turn override off, keep amount
                  </ListBoxItem>
                  <ListBoxItem id="DELETE">
                    DELETE — turn override off and clear amount
                  </ListBoxItem>
                </Select>
              </div>

              <div className={styles.field}>
                <TextInput
                  label="EmployeeCode"
                  value={draft.employeeCode}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, employeeCode: value } : prev
                    )
                  }
                  isRequired
                  isDisabled={isProcessing}
                />
              </div>

              <div className={styles.field}>
                <TextInput
                  label="ComponentCode"
                  value={draft.componentCode}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, componentCode: value } : prev
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>

              <div className={styles.field}>
                <TextInput
                  label="ComponentName"
                  value={draft.componentName}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, componentName: value } : prev
                    )
                  }
                  isRequired
                  isDisabled={isProcessing}
                />
              </div>

              <div className={styles.field}>
                <TextInput
                  label="StartDate"
                  value={draft.startDate}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, startDate: value } : prev
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>

              <div className={styles.field}>
                <TextInput
                  label="FinishDate"
                  value={draft.finishDate}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, finishDate: value } : prev
                    )
                  }
                  isDisabled={isProcessing}
                />
              </div>

              <div className={styles.field}>
                <TextInput
                  label="TargetAmount"
                  value={draft.targetAmount}
                  onChange={(value) =>
                    setDraft((prev) =>
                      prev ? { ...prev, targetAmount: value } : prev
                    )
                  }
                  isRequired={draft.matchAction === "APPLY"}
                  isDisabled={isProcessing}
                />
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div className={styles.fieldErrors} role="alert">
                <strong>Fix these before saving</strong>
                <ul className={styles.issueList}>
                  {validationErrors.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <ButtonGroup>
              <Button variant="tertiary" onPress={handleCancel} isDisabled={isProcessing}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onPress={handleSave}
                isDisabled={!isDirty || isProcessing || validationErrors.length > 0}
              >
                Save changes
              </Button>
            </ButtonGroup>
          </div>
        </div>
      </Modal>

      <UnsavedProgressConfirmModal
        isOpen={discardOpen}
        title="Discard row edits?"
        message="Unsaved changes to this row will be lost."
        onStay={() => setDiscardOpen(false)}
        onLeave={() => {
          setDiscardOpen(false);
          onCancel();
        }}
      />
    </>
  );
}
